import { isInvoicingSessionCancelled } from "components/Dashboard/DoctorDashboard/utils/isInvoicingSessionCancelled";
import getItemsByTime, {
  TYPE_FILTER_OPTIONS,
} from "components/Dashboard/utility/getItemsByTime";
import Loading from "components/Loading";
import AppPagination from "components/app-pagination";
import FilterButton from "components/filter-button";
import LazyImage from "components/lazy-image";
import { useUser } from "contexts/UserContext";
import useFetchInvoicesForAppointments from "hooks/useFetchInvoicesForAppointments";
import useFetchSuperbillsForAppointments from "hooks/useFetchSuperbillsForAppointments";
import messages from "messages";
import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { AmplitudePageName, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import styles from "./ListView.module.scss";
import { useInfiniteQuery } from "@tanstack/react-query";
import { appointmentsV2InfiniteQueryOptions } from "queries/appointmentsV2QueryOptions";
import useFetchAllPagesForInfiniteQuery from "queries/useFetchAllPagesForInfiniteQuery";

const CANCELLED_SESSION_EMPTY_STATE =
  "/img/dashboard/cancelled-session-empty-state.png";
const COMPLETED_SESSION_EMPTY_STATE =
  "/img/dashboard/completed-session-empty-state.png";
const UPCOMING_SESSION_EMPTY_STATE =
  "/img/dashboard/upcoming-session-empty-state.png";

const COUNT_PER_PAGE = 10;

interface Props {
  amplitudePageName: AmplitudePageName;
  amplitudeComponentName: string;
  showPast?: boolean;
  showCancelled?: boolean;
}

const ListView: React.FC<Props> = ({
  amplitudePageName,
  amplitudeComponentName,
  showPast,
  showCancelled,
}) => {
  const appointmentsQuery = useInfiniteQuery(
    appointmentsV2InfiniteQueryOptions({}),
  );
  useFetchAllPagesForInfiniteQuery(appointmentsQuery);

  const appointments = useMemo(
    () =>
      appointmentsQuery.data?.pages.flatMap((page) => page.appointments) ?? [],
    [appointmentsQuery.data?.pages],
  );

  const { appointmentsWithSuperBill, isLoadingSuperbills } =
    useFetchSuperbillsForAppointments(appointments || []);

  const {
    appointmentsWithInvoices: appointmentsWithSuperbillAndInvoices,
    isLoadingInvoices,
  } = useFetchInvoicesForAppointments(appointmentsWithSuperBill);

  const isLoading = useMemo(
    () =>
      appointmentsQuery.isLoading ||
      appointmentsQuery.isPending ||
      appointmentsQuery.hasNextPage ||
      isLoadingInvoices ||
      isLoadingSuperbills,
    [
      appointmentsQuery.isLoading,
      appointmentsQuery.isPending,
      appointmentsQuery.hasNextPage,
      isLoadingInvoices,
      isLoadingSuperbills,
    ],
  );

  const { defaultUserTimezone } = useUser();

  // filters
  const [typeFilter, setTypeFilter] = useState<{ id: string; value: string }>();

  // appts by time
  const [items, setItems] = useState(
    getItemsByTime(appointmentsWithSuperbillAndInvoices, [], +new Date()),
  );
  // page start, starts with 0
  const [page, setPage] = useState(0);

  useEffect(() => {
    setItems(
      getItemsByTime(appointmentsWithSuperbillAndInvoices, [], +new Date()),
    );
    const timer = setInterval(() => {
      setItems(
        getItemsByTime(appointmentsWithSuperbillAndInvoices, [], +new Date()),
      );
      // 5 mins
    }, 300000);
    return () => {
      clearInterval(timer);
    };
  }, [appointmentsWithSuperbillAndInvoices]);

  const cancelledItems = items.filter(
    (el) =>
      (el.typeId === "ONE_ON_ONE" && el.statusFlags.cancelledOrAbandoned) ||
      (el.typeId === "ONE_ON_ONE" && isInvoicingSessionCancelled(el, null)),
  );

  const validItems = items.filter(
    (el) =>
      !cancelledItems.includes(el) &&
      (el.typeId !== "ONE_ON_ONE" || !el.statusFlags.unconfirmedInvite),
  );

  const itemsFiltered = showCancelled
    ? cancelledItems
    : showPast
    ? validItems.filter((el) => el.isPast)
    : validItems.filter((el) => !el.isPast);

  const itemsInList = useMemo(() => {
    let nextItems = itemsFiltered;

    if (typeFilter) {
      nextItems = nextItems.filter((el) => el.typeId === typeFilter.id);
    }

    return nextItems;
  }, [itemsFiltered, typeFilter]);

  if (itemsFiltered.length === 0 && isLoading) {
    return (
      <div className={styles["list-view-empty-state"]}>
        <Loading />
      </div>
    );
  }

  if (itemsFiltered.length === 0) {
    return showCancelled ? (
      <div className={styles["list-view-empty-state"]}>
        <LazyImage
          src={CANCELLED_SESSION_EMPTY_STATE}
          width={168}
          height={"auto"}
        />
        <div className={styles["list-view-empty-state__title"]}>
          No Cancelled Sessions yet.
        </div>
        <div className={styles["list-view-empty-state__sub-title"]}>
          All session details will appear here.
        </div>
      </div>
    ) : showPast ? (
      <div className={styles["list-view-empty-state"]}>
        <LazyImage
          src={COMPLETED_SESSION_EMPTY_STATE}
          width={168}
          height={"auto"}
        />
        <div className={styles["list-view-empty-state__title"]}>
          No Completed Sessions yet.
        </div>
        <div className={styles["list-view-empty-state__sub-title"]}>
          All session details will appear here.
        </div>
      </div>
    ) : (
      <div className={styles["list-view-empty-state"]}>
        <LazyImage
          src={UPCOMING_SESSION_EMPTY_STATE}
          width={168}
          height={"auto"}
        />
        <div className={styles["list-view-empty-state__title"]}>
          No Upcoming Sessions yet.
        </div>
        <div className={styles["list-view-empty-state__sub-title"]}>
          All session details will appear here.
        </div>
      </div>
    );
  }

  return (
    <div className={styles["list-view"]}>
      <Table
        className={getClasses(
          styles["list-view__table"],
          showCancelled && styles["list-view__table-cancelled"],
        )}
      >
        {/* title row */}
        <thead>
          <tr>
            {/* time */}
            <th>{messages.sessionDate}</th>
            {/* session name */}
            <th>{messages.sessionName}</th>
            {/* session type */}
            <th>
              Type
              <FilterButton
                options={Object.values(TYPE_FILTER_OPTIONS)}
                filter={typeFilter}
                onApply={(el) => setTypeFilter(el)}
              />
            </th>
            <th>{messages.patientName}</th>
            {/* {showCancelled && <th>Cancellation Time</th>} */}
            {showCancelled && <th>Cancellation Policy</th>}
            <th>Amount Paid</th>
          </tr>
        </thead>
        <tbody>
          {itemsInList
            .slice(COUNT_PER_PAGE * page, COUNT_PER_PAGE * (page + 1))
            .map((el, i) => (
              <tr key={`${i}-${el.timeslotId}`}>
                {/* time */}
                <td>
                  {`${new Date(el.date).toLocaleDateString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })} ${moment.tz(defaultUserTimezone).zoneAbbr()}`}
                </td>
                {/* session name */}
                <td>{el.title}</td>
                {/* session type */}
                <td>{TYPE_FILTER_OPTIONS[el.typeId].value}</td>
                {/* patient name */}
                {el.typeId === "ONE_ON_ONE" ? (
                  <td>
                    <Link
                      to={`/pro-dashboard/patient-charts/${el.patient?.cognito_sub}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        track(
                          amplitudePageName,
                          [amplitudeComponentName, "patient-link"],
                          "click",
                        )
                      }
                    >
                      {el.attendees}
                    </Link>
                  </td>
                ) : (
                  <td>{`${el.attendees} participant(s)`}</td>
                )}
                {/* {showCancelled && <td>TODO: timestamp</td>} */}
                {showCancelled && el.typeId === "ONE_ON_ONE" && (
                  <td>
                    {el.cancellationPolicy.cancellation_policy_flat_fee && (
                      <div
                        className={styles["list-view__table-col-cancel-amount"]}
                      >{`$${el.cancellationPolicy.cancellation_policy_flat_fee} flat fee`}</div>
                    )}
                    {el.cancellationPolicy.cancellation_policy_percent_fee && (
                      <div
                        className={styles["list-view__table-col-cancel-amount"]}
                      >{`${el.cancellationPolicy.cancellation_policy_percent_fee}% rate`}</div>
                    )}
                    {(!el.cancellationPolicy ||
                      el.cancellationPolicy.cancellation_notice_hours ===
                        null ||
                      el.cancellationPolicy.cancellation_notice_hours === 0) &&
                      "-"}
                    <div className={styles["list-view__table-col-cancel-hour"]}>
                      {el.cancellationPolicy.cancellation_notice_hours === 0
                        ? "No cancellation policy"
                        : el.cancellationPolicy.cancellation_notice_hours &&
                          `if cancelled in ${el.cancellationPolicy.cancellation_notice_hours} hours`}
                    </div>
                  </td>
                )}
                <td>
                  {el.typeId === "ONE_ON_ONE"
                    ? el.statusFlags.usingInvoicing // invoicing session
                      ? `$${(el.invoice?.amount_total_paid || 0).toFixed(2)}`
                      : showCancelled
                      ? el.cancelRefundOffer &&
                        `$${el.cancelRefundOffer.cancellation_price?.toFixed(
                          2,
                        )}`
                      : el.pricing &&
                        `$${el.pricing.amount_paid_by_patient?.toFixed(2)}`
                    : showCancelled
                    ? "-"
                    : `$${(el.price || 0).toFixed(2)}`}
                </td>
                {/* <td>
                  {el.status}
                  {el.timeslotId}
                </td> */}
              </tr>
            ))}
          {isLoading && (
            <tr>
              <td colSpan={6} className="text-center">
                <Loading />
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* pagination */}
      {/* show 10 rows of data per page  */}
      {itemsInList.length > COUNT_PER_PAGE && (
        <AppPagination
          className={styles["list-view__pager"]}
          page={page + 1}
          onPageChange={(p) => setPage(p - 1)}
          dataCount={itemsInList.length}
          dataCountPerPage={COUNT_PER_PAGE}
        />
      )}
    </div>
  );
};
export default ListView;
